import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { LabelsService } from 'src/app/util/services/labels/labels.service';
import { EquipmentHistoryReportService, EquipmentHistoryRow } from './service/equipment-history-report.service';
import { mspTableHeaderI } from 'src/app/page_component_item/msp-table/msp-table.component';
import { ExcelService } from 'src/app/services/excel/excel.service';
import { DateService } from 'src/app/services/date/date.service';
import { BreadcrumbNavigationComponent } from 'src/app/page_component_item/breadcrumb-navigation/breadcrumb-navigation.component';
import { MspTableRmkComponent } from 'src/app/page_component_item/msp-table-rmk/msp-table-rmk.component';
import { PaginationCounterModule } from 'src/app/page_component_item/pagination-counter/pagination-counter.module';
import { Subscription, finalize, timeout } from 'rxjs';

@Component({
	standalone: true,
	imports: [CommonModule, FontAwesomeModule, BreadcrumbNavigationComponent, MspTableRmkComponent, PaginationCounterModule],
	selector: 'app-equipment-history-report',
	templateUrl: './equipment-history-report.component.html',
	styleUrls: ['./equipment-history-report.component.scss'],
})
export class EquipmentHistoryReportComponent {
	icons = { faMagnifyingGlass };
	labels: any = {
		lbl_geral_reports: 'Relatórios',
		lbl_geral_voltar: 'Voltar',
		lbl_geral_pesquisar: 'Pesquisar',
		lbl_geral_usuario: 'Usuário',
		lbl_geral_nome_completo: 'Nome',
		lbl_geral_email: 'E-mail',
		lbl_geral_login: 'Login',
		lbl_cmdb: 'CMDB',
		lbl_geral_identificacao: 'Identificador',
		lbl_geral_numero_serie: 'Número de Série',
		lbl_geral_data_recebimento: 'Data de Recebimento',
		lbl_geral_data: 'Data',
		lbl_geral_data_retorno: 'Data de Devolução',
		lbl_termo_responsabilidade_assinado: 'Termo de Responsabilidade Assinado',
		lbl_termo_devolucao_assinado: 'Termo de Devolução Assinado',
		lbl_geral_com_usuario: 'Está com o Usuário',
		lbl_geral_sim: 'Sim',
		lbl_geral_nao: 'Não',
		lbl_edocs_carregando: 'Carregando',
		lbl_security_nenhum_registro: 'Nenhum registro encontrado.',
		lbl_informe_pesquisa: 'Informe nome, e-mail ou login para pesquisar.',
	};

	search = '';
	allHistory: EquipmentHistoryRow[] = [];
	history: EquipmentHistoryRow[] = [];
	displayedHistory: EquipmentHistoryRow[] = [];
	totalHistory = 0;
	isLoadingHistory = false;
	historyRequest?: Subscription;
	itemsPerPage = 20;
	currentPage = 1;
	tableSort = { sortBy: 'received_date', sortOrder: 'desc' as 'desc' | 'asc', sortDown: true };

	headers: mspTableHeaderI[] = [
		{ name: 'user_full_name', value: 'lbl_geral_usuario', colType: 'text', colWidth: 'minmax(14rem, 1.5fr)' },
		{ name: 'ci_id', value: 'lbl_cmdb', colType: 'text', colWidth: '7rem' },
		{ name: 'identification', value: 'lbl_geral_identificacao', colType: 'text', colWidth: '12rem' },
		{ name: 'serial_number', value: 'Número de Série', colType: 'text', colWidth: '12rem' },
		{ name: 'received_date', value: 'Data de Recebimento', colType: 'date', colWidth: '10rem' },
		{ name: 'returned_date', value: 'lbl_geral_data_retorno', colType: 'date', colWidth: '10rem' },
		{ name: 'responsibility_signed_date', value: 'lbl_termo_responsabilidade_assinado', colType: 'date', colWidth: '12rem' },
		{ name: 'devolution_signed_date', value: 'lbl_termo_devolucao_assinado', colType: 'date', colWidth: '12rem' },
		{ name: 'is_with_user', value: 'Com o Usuário', colType: 'text', colWidth: '9rem' },
	];

	constructor(
		private labelsService: LabelsService,
		private reportService: EquipmentHistoryReportService,
		private excelService: ExcelService,
		private dateService: DateService
	) {
		this.labelsService.getLabels().subscribe((labels) => (this.labels = { ...this.labels, ...labels }));
		this.searchHistory();
	}

	searchHistory(): void {
		this.loadHistory(1);
	}

	updateDisplayedHistory(): void {
		this.displayedHistory = [...this.history];
	}

	setPage(page: number): void {
		const lastPage = Math.max(1, Math.ceil(this.totalHistory / this.itemsPerPage));
		this.loadHistory(Math.min(page, lastPage));
	}

	private loadHistory(page: number): void {
		this.currentPage = page;
		if (this.historyRequest) this.historyRequest.unsubscribe();
		this.isLoadingHistory = true;
		const shouldRecount = page === 1;
		const historyRequest = this.reportService
			.getHistory(this.search, page, this.itemsPerPage, shouldRecount)
			.pipe(
				timeout(30_000),
				finalize(() => (this.isLoadingHistory = false))
			)
			.subscribe({
				next: (response) => {
					this.allHistory = (response.data || []).map((row) => ({
						...row,
						is_with_user: this.isWithUser(row) ? this.labels.lbl_geral_sim : this.labels.lbl_geral_nao,
					}));
					if (response.total_count >= 0) this.totalHistory = response.total_count;
					this.history = [...this.allHistory];
					this.updateDisplayedHistory();
				},
				error: () => {
					this.allHistory = [];
					this.history = [];
					this.totalHistory = 0;
					this.updateDisplayedHistory();
				},
			});
		this.historyRequest = historyRequest;
	}

	setGeneralSearch(event: Event): void {
		this.search = (event.target as HTMLInputElement).value;
	}

	applyTableSearch(search: { id: string; values: any[] }): void {
		const value = String(search.values?.[0] || '')
			.trim()
			.toLowerCase();
		this.history = value
			? this.allHistory.filter((row) =>
					String(row[search.id] || '')
						.toLowerCase()
						.includes(value)
				)
			: [...this.allHistory];
		this.currentPage = 1;
		this.updateDisplayedHistory();
	}

	clearTableSearch(): void {
		this.search = '';
		this.searchHistory();
	}

	exportHistory(): void {
		const exportHeaders = this.headers.map((header) => ({
			...header,
			value: this.labels[header.value] || header.value,
		}));
		const exportBody = this.history.map((row) =>
			this.headers.reduce((formattedRow, header) => {
				formattedRow[header.name] = header.colType === 'date' ? this.dateService.formatDate(row[header.name]) : row[header.name];
				return formattedRow;
			}, {} as EquipmentHistoryRow)
		);
		const exportRows = this.excelService.convertMspTableInto2d(exportHeaders, exportBody);
		this.excelService.exportAsExcelFile(exportRows, 'historico-equipamentos-usuario');
	}

	applySort(sort: typeof this.tableSort): void {
		this.tableSort = sort;
		this.history.sort((first, second) => {
			const firstValue = first[sort.sortBy] || '';
			const secondValue = second[sort.sortBy] || '';
			if (firstValue === secondValue) return 0;
			const result = firstValue > secondValue ? 1 : -1;
			return sort.sortOrder === 'asc' ? result : -result;
		});
		this.updateDisplayedHistory();
	}

	formatDate(value: string): string {
		if (!value) return '';
		const date = new Date(value);
		return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
	}

	isWithUser(row: EquipmentHistoryRow): boolean {
		return (
			row.is_with_user === true ||
			row.is_with_user === 1 ||
			row.is_with_user === ('1' as any) ||
			row.is_with_user === this.labels.lbl_geral_sim
		);
	}
}
